import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { IconControl, Loading, TabPaneControl } from "spark-core-dx/components";
import { useCoreRoute, useGenericManager } from "spark-core-dx/hooks";
import AIPanel from "../../../components/AIChat/AIPanel";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import "./ediDiagnosticsManage.scss";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "diagnostics", label: "Diagnostics" },
  { key: "fields", label: "Source Fields" },
  { key: "raw", label: "Raw Context" },
];

const PANEL_TRANSITION_MS = 240;

const asArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value.toArray instanceof Function) return value.toArray();
  if (typeof value[Symbol.iterator] === "function") return Array.from(value);
  if (value.first instanceof Function) return [value.first()].filter(Boolean);
  return [];
};

const firstItem = (result) => {
  const normalized = Array.isArray(result) ? result[0] : result;
  const items = asArray(normalized?.Items || normalized?.items);
  return items[0] ?? null;
};

const text = (value) => String(value ?? "").trim();

const getProp = (item, ...keys) => {
  if (!item || typeof item !== "object") return undefined;
  for (const key of keys) {
    const value = item[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const getPanelParam = (params, ...keys) => {
  if (!params || typeof params !== "object") return "";
  for (const key of keys) {
    const value = params[key];
    if (text(value)) return text(value);
  }
  return "";
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const formatElapsed = (value) => {
  if (!value) return "-";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const totalMs = Number(value.TotalMilliseconds ?? value.totalMilliseconds);
    if (!Number.isNaN(totalMs) && totalMs > 0) return `${Math.round(totalMs)}ms`;
  }
  return String(value);
};

const normalizeLevel = (value) => {
  const level = String(value || "Info").toLowerCase();
  if (level === "error" || level === "errors" || level === "4") return "error";
  if (level === "warning" || level === "warnings" || level === "3") return "warning";
  return "info";
};

const stripProcessPath = (value) => {
  if (value === undefined || value === null) return "";
  return String(value)
    .replace(/,?\s*"ProcessPath"\s*:\s*"(?:\\.|[^"\\])*"\s*,?/gi, "")
    .replace(/(^|[,\r\n;]\s*)ProcessPath\s*:\s*[^,\r\n;]*/gi, (match, prefix) => (
      prefix?.startsWith(",") ? "" : prefix || ""
    ))
    .replace(/,,/g, ",")
    .replace(/\{\s*,/g, "{")
    .replace(/,\s*\}/g, "}")
    .trim()
    .replace(/^,|,$/g, "");
};

const sanitizeDisplayObject = (value) => {
  if (Array.isArray(value)) return value.map(sanitizeDisplayObject);
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, entry]) => {
      if (key.toLowerCase() === "processpath") return acc;
      acc[key] = sanitizeDisplayObject(entry);
      return acc;
    }, {});
  }
  if (typeof value === "string") return stripProcessPath(value);
  return value;
};

const EDIDiagnosticsManage = ({
  panelMode = false,
  panelOpen = true,
  panelId = "",
  panelSessionId = "",
  panelParams = null,
  onPanelClose = null,
}) => {
  const { sourceObjectId: routeSourceObjectId, id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const { coreNavigate } = useCoreRoute();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const itemServiceEnum = itemServiceFactory?.ItemServiceEnum ?? {
    SourceObject: "SourceObject",
    AIChatSession: "AIChatSession",
  };
  const sourceObjectMgr = useGenericManager(itemServiceEnum.SourceObject);
  const chatSessionMgr = useGenericManager(itemServiceEnum.AIChatSession);
  const managersRef = useRef({ sourceObjectMgr, chatSessionMgr });

  const sourceObjectId = useMemo(() => (
    text(panelId) ||
    getPanelParam(panelParams, "sourceObjectId", "SourceObjectId", "soId", "id", "_id") ||
    text(searchParams.get("sourceObjectId")) ||
    text(searchParams.get("id")) ||
    text(searchParams.get("aiPanelId")) ||
    text(routeSourceObjectId) ||
    text(routeId)
  ), [panelId, panelParams, routeId, routeSourceObjectId, searchParams]);

  const initialValidationEventId = useMemo(() => (
    getPanelParam(panelParams, "validationEventId", "ValidationEventId", "eventId", "EventId") ||
    text(searchParams.get("validationEventId")) ||
    text(searchParams.get("eventId"))
  ), [panelParams, searchParams]);

  const [selectedValidationEventId, setSelectedValidationEventId] = useState(initialValidationEventId);
  const [diagnosticsContext, setDiagnosticsContext] = useState(null);
  const [session, setSession] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [rerunning, setRerunning] = useState(false);
  const [error, setError] = useState("");
  const [panelContentReady, setPanelContentReady] = useState(!panelMode);
  const loadKeyRef = useRef("");
  const loadInFlightRef = useRef("");

  useEffect(() => {
    managersRef.current = { sourceObjectMgr, chatSessionMgr };
  }, [chatSessionMgr, sourceObjectMgr]);

  useEffect(() => {
    setSelectedValidationEventId(initialValidationEventId);
    loadKeyRef.current = "";
  }, [initialValidationEventId, sourceObjectId]);

  useEffect(() => {
    if (!panelMode) {
      setPanelContentReady(true);
      return undefined;
    }

    if (!panelOpen) {
      setPanelContentReady(false);
      return undefined;
    }

    setPanelContentReady(false);
    const timer = window.setTimeout(() => setPanelContentReady(true), PANEL_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [panelMode, panelOpen]);

  const getChatSession = useCallback(async (referenceId) => {
    if (!referenceId) return null;
    if (panelSessionId) {
      return {
        _id: panelSessionId,
        ReferenceId: referenceId,
        ReferenceItemName: "EDIDiagnostics",
      };
    }

    const result = await managersRef.current.chatSessionMgr.Query({
      model: "x => x.ReferenceId == referenceId && x.ReferenceItemName == referenceItemName && x.IsDeleted == false && x.IsActive == true",
      params: {
        referenceId,
        referenceItemName: "EDIDiagnostics",
      },
    });

    if (!result?.Success || !Array.isArray(result?.Items) || result.Items.length === 0) return null;
    return [...result.Items].sort((left, right) => {
      const rightDate = Date.parse(right?.UpdatedDate ?? right?.CreatedDate ?? "") || Number(right?.Sequence ?? 0);
      const leftDate = Date.parse(left?.UpdatedDate ?? left?.CreatedDate ?? "") || Number(left?.Sequence ?? 0);
      return rightDate - leftDate;
    })[0];
  }, [panelSessionId]);

  const loadDiagnosticsContext = useCallback(async (eventIdOverride = selectedValidationEventId, force = false) => {
    if (panelMode && !panelContentReady) return;

    if (!sourceObjectId) {
      setLoading(false);
      setError("Source object id is required.");
      return;
    }

    const diagnosticsEventId = text(eventIdOverride);
    const loadKey = `${sourceObjectId}:${diagnosticsEventId || ""}`;
    if (!force && (loadInFlightRef.current === loadKey || loadKeyRef.current === loadKey)) return;

    loadInFlightRef.current = loadKey;
    setLoading(true);
    setError("");
    try {
      const [contextResult, nextSession] = await Promise.all([
        managersRef.current.sourceObjectMgr.RunOperation({
          operationName: "LoadEDIDiagnosticsContext",
          id: sourceObjectId,
          queryParams: {
            sourceObjectId,
            validationEventId: diagnosticsEventId,
          },
        }),
        getChatSession(sourceObjectId),
      ]);

      const contextItem = firstItem(contextResult) || contextResult?.Value || contextResult?.value;
      if (!contextItem) throw new Error("EDI diagnostics context was not returned.");
      setDiagnosticsContext(contextItem);
      setSession(nextSession);
      loadKeyRef.current = loadKey;
    } catch (err) {
      setError(err?.message || "Failed to load EDI diagnostics context.");
    } finally {
      loadInFlightRef.current = "";
      setLoading(false);
    }
  }, [getChatSession, panelContentReady, panelMode, selectedValidationEventId, sourceObjectId]);

  useEffect(() => {
    void loadDiagnosticsContext();
  }, [loadDiagnosticsContext]);

  const handleRerunDiagnostics = useCallback(async () => {
    if (!sourceObjectId || rerunning) return;

    setRerunning(true);
    setError("");
    try {
      const result = await managersRef.current.sourceObjectMgr.RunOperation({
        operationName: "ValidateSourceObjectKeys",
        id: sourceObjectId,
        queryParams: {
          mode: "manage",
          forceRun: "true",
          rerun: "true",
          force: "true",
          rerunAt: String(Date.now()),
        },
      });

      if (result?.Success === false) {
        throw new Error(result?.DisplayMessage || result?.Message?.DisplayMessage || "EDI diagnostics rerun failed.");
      }

      const value = result?.Value ?? result?.value ?? {};
      const nextValidationEventId = text(value.ValidationEventId || value.validationEventId || value.EventId || value.eventId);
      loadKeyRef.current = "";
      setSelectedValidationEventId(nextValidationEventId);
      setActiveTab("overview");
      await loadDiagnosticsContext(nextValidationEventId, true);
    } catch (err) {
      setError(err?.message || "Failed to rerun EDI diagnostics.");
    } finally {
      setRerunning(false);
    }
  }, [loadDiagnosticsContext, rerunning, sourceObjectId]);

  const handleCancel = () => {
    if (panelMode && typeof onPanelClose === "function") {
      onPanelClose();
      return;
    }

    coreNavigate("/dynamic/sourceobject/");
  };

  const diagnostics = asArray(getProp(diagnosticsContext, "Diagnostics", "diagnostics"));
  const fields = asArray(getProp(diagnosticsContext, "SourceFields", "sourceFields"));
  const status = getProp(diagnosticsContext, "Status", "status") || "No Diagnostics";
  const humanReportHtml = stripProcessPath(getProp(diagnosticsContext, "HumanReportHtml", "humanReportHtml"));
  const aiContextMarkdown = stripProcessPath(getProp(diagnosticsContext, "AIContextMarkdown", "aiContextMarkdown"));

  const renderOverview = () => (
    <div className="edi-diagnostics-grid">
      <section className="edi-diagnostics-panel edi-diagnostics-panel--wide">
        <div className="edi-diagnostics-panel__title">Source Object</div>
        <div className="edi-diagnostics-title">{getProp(diagnosticsContext, "SourceObjectName", "sourceObjectName") || "EDI source object"}</div>
        <div className="edi-diagnostics-muted">
          {getProp(diagnosticsContext, "SourceSystemName", "sourceSystemName") || "-"} - {getProp(diagnosticsContext, "SourceSystemType", "sourceSystemType") || "EDI"}
        </div>
      </section>
      <section className="edi-diagnostics-panel">
        <div className="edi-diagnostics-panel__title">Status</div>
        <div className={`edi-diagnostics-stat edi-diagnostics-stat--${normalizeLevel(status)}`}>{status}</div>
      </section>
      <section className="edi-diagnostics-panel">
        <div className="edi-diagnostics-panel__title">Diagnostics</div>
        <div className="edi-diagnostics-stat">{getProp(diagnosticsContext, "DiagnosticCount", "diagnosticCount") ?? diagnostics.length}</div>
      </section>
      <section className="edi-diagnostics-panel">
        <div className="edi-diagnostics-panel__title">Errors</div>
        <div className="edi-diagnostics-stat edi-diagnostics-stat--error">{getProp(diagnosticsContext, "ErrorCount", "errorCount") ?? 0}</div>
      </section>
      <section className="edi-diagnostics-panel">
        <div className="edi-diagnostics-panel__title">Warnings</div>
        <div className="edi-diagnostics-stat edi-diagnostics-stat--warning">{getProp(diagnosticsContext, "WarningCount", "warningCount") ?? 0}</div>
      </section>
      <section className="edi-diagnostics-panel edi-diagnostics-panel--wide">
        <div className="edi-diagnostics-panel__title">Validation Run</div>
        <div className="edi-diagnostics-muted">
          Event: {getProp(diagnosticsContext, "ValidationEventId", "validationEventId") || getProp(diagnosticsContext, "LatestDiagnosticsEventId", "latestDiagnosticsEventId") || "-"}
        </div>
        <div className="edi-diagnostics-muted">Loaded: {formatDate(getProp(diagnosticsContext, "GeneratedUtc", "generatedUtc"))}</div>
      </section>
      {humanReportHtml ? (
        <section className="edi-diagnostics-report edi-diagnostics-panel--wide" dangerouslySetInnerHTML={{ __html: humanReportHtml }} />
      ) : null}
    </div>
  );

  const renderDiagnostics = () => {
    if (diagnostics.length === 0) {
      return <div className="edi-diagnostics-empty">No EDI diagnostics were reported for this validation run.</div>;
    }

    return (
      <div className="edi-diagnostics-list">
        {diagnostics.map((log, index) => {
          const level = getProp(log, "LogLevelName", "logLevelName", "LogLevel", "logLevel") || "Info";
          return (
            <section className="edi-diagnostics-log" key={`${getProp(log, "LogId", "logId") || index}`}>
              <div className="edi-diagnostics-log__header">
                <span className={`edi-diagnostics-badge edi-diagnostics-badge--${normalizeLevel(level)}`}>{String(level)}</span>
                <span>{getProp(log, "EventName", "eventName") || "Validation"}</span>
                <span>{formatElapsed(getProp(log, "Elapsed", "elapsed"))}</span>
              </div>
              <div className="edi-diagnostics-log__messages">
                {asArray(getProp(log, "Messages", "messages"))
                  .map(stripProcessPath)
                  .filter(Boolean)
                  .map((message, msgIndex) => (
                    <div key={`${message}-${msgIndex}`}>{message}</div>
                  ))}
              </div>
              {getProp(log, "SystemInfo", "systemInfo") ? (
                <pre className="edi-diagnostics-code">{stripProcessPath(getProp(log, "SystemInfo", "systemInfo"))}</pre>
              ) : null}
            </section>
          );
        })}
      </div>
    );
  };

  const renderFields = () => {
    if (fields.length === 0) {
      return <div className="edi-diagnostics-empty">No source fields are available on this source object.</div>;
    }

    return (
      <div className="edi-diagnostics-table-wrap">
        <table className="edi-diagnostics-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>System</th>
              <th>Type</th>
              <th>Key</th>
              <th>Required</th>
              <th>Disabled</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => {
              const fieldId = getProp(field, "SourceObjectFieldId", "sourceObjectFieldId");
              return (
                <tr key={fieldId}>
                  <td>
                    <strong>{getProp(field, "DisplayName", "displayName") || getProp(field, "FieldName", "fieldName")}</strong>
                    <div className="edi-diagnostics-muted">{getProp(field, "FieldName", "fieldName")}</div>
                  </td>
                  <td>{getProp(field, "SystemName", "systemName") || "-"}</td>
                  <td>{getProp(field, "SystemDataType", "systemDataType") || getProp(field, "DataTypeName", "dataTypeName") || "-"}</td>
                  <td>{getProp(field, "IsPrimary", "isPrimary") ? "Primary" : getProp(field, "IsUniqueKey", "isUniqueKey") ? "Unique" : "-"}</td>
                  <td>{getProp(field, "IsRequired", "isRequired") ? "Yes" : "No"}</td>
                  <td>{getProp(field, "IsDisabled", "isDisabled") ? "Yes" : "No"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRawContext = () => (
    <div className="edi-diagnostics-raw">
      <section>
        <div className="edi-diagnostics-section-title">AI Context Markdown</div>
        <pre className="edi-diagnostics-code">{aiContextMarkdown || "No raw AI context was returned."}</pre>
      </section>
      <section>
        <div className="edi-diagnostics-section-title">Context JSON</div>
        <pre className="edi-diagnostics-code">{JSON.stringify(sanitizeDisplayObject(diagnosticsContext ?? {}), null, 2)}</pre>
      </section>
    </div>
  );

  const renderActiveTab = (tabKey = activeTab) => {
    if (tabKey === "diagnostics") return renderDiagnostics();
    if (tabKey === "fields") return renderFields();
    if (tabKey === "raw") return renderRawContext();
    return renderOverview();
  };

  const tabs = TABS.map((tab) => ({
    key: tab.key,
    title: tab.label,
    content: <main className="edi-diagnostics-content">{renderActiveTab(tab.key)}</main>,
  }));

  const handleTabChange = (nextIndexOrKey, tab, nextKey) => {
    const resolvedKey = typeof nextKey === "string"
      ? nextKey
      : typeof nextIndexOrKey === "number"
        ? TABS[nextIndexOrKey]?.key
        : typeof nextIndexOrKey === "string"
          ? nextIndexOrKey
          : tab?.key || nextIndexOrKey?.key;

    setActiveTab(resolvedKey || "overview");
  };

  const rerunButton = (
    <button
      type="button"
      className="edi-diagnostics-header-action"
      onClick={handleRerunDiagnostics}
      disabled={rerunning || !sourceObjectId}
      aria-label={rerunning ? "Rerunning EDI diagnostics" : "Rerun EDI diagnostics"}
      title={rerunning ? "Rerunning diagnostics" : "Rerun diagnostics"}
    >
      {rerunning ? (
        <span className="edi-diagnostics-header-action__spinner" aria-hidden="true">
          <Loading />
        </span>
      ) : (
        <IconControl iconKey="refresh" width="1rem" height="1rem" />
      )}
    </button>
  );

  const content = (
    <div className="edi-diagnostics-screen">
      <header className="edi-diagnostics-header">
        <div className="edi-diagnostics-header-main">
          <div className="edi-diagnostics-eyebrow">EDI Diagnostics</div>
          <h1>{getProp(diagnosticsContext, "SourceObjectName", "sourceObjectName") || "EDI Source Object"}</h1>
          <div className="edi-diagnostics-meta">
            <span>{getProp(diagnosticsContext, "SourceSystemName", "sourceSystemName") || "Source system"}</span>
            <span>{status}</span>
            <span>{fields.length} fields</span>
            <span>{diagnostics.length} diagnostics</span>
          </div>
        </div>
        <div className="edi-diagnostics-header-actions">
          {rerunButton}
        </div>
      </header>

      {error ? <div className="edi-diagnostics-warning">{error}</div> : null}

      <div className="edi-diagnostics-tab-shell">
        <TabPaneControl
          className="ai-panel-tabs edi-diagnostics-tabs"
          tabs={tabs}
          activeIndex={Math.max(0, TABS.findIndex((tab) => tab.key === activeTab))}
          onChange={handleTabChange}
          compact={true}
          framed={false}
          unmountInactive={true}
        />
      </div>
    </div>
  );

  return (
    <AIPanel
      open={panelMode ? panelOpen : true}
      presentation={panelMode ? "slideout" : "inline"}
      mapToClass="core-layout"
      title="Analyze EDI Diagnostics"
      minPanelWidth={1040}
      minLeft={520}
      minRight={400}
      width={panelMode ? 1180 : undefined}
      panelStorageKey={panelMode ? "ai-panel:edi-diagnostics:width" : undefined}
      contentReady={panelMode ? panelContentReady : true}
      loadingContent={<div className="edi-diagnostics-loading"><Loading /></div>}
      params={{ sourceObjectId, validationEventId: selectedValidationEventId }}
      payload={diagnosticsContext}
      referenceId={sourceObjectId}
      referenceName="EDIDiagnostics"
      session={session}
      operationName="AIQuestionEDIDiagnostics"
      introMessage="Analyze EDI Diagnostics..."
      splashMessage="How can I help you understand this EDI issue?"
      singleSessionMode={true}
      defaultOrchestrationMode="Direct"
      planningPreviewEnabled={false}
      previewPanelEnabled={false}
      previewRefreshEnabled={false}
      onCancel={handleCancel}
    >
      {loading ? (
        <div className="edi-diagnostics-screen edi-diagnostics-screen--loading">
          <Loading />
        </div>
      ) : content}
    </AIPanel>
  );
};

export default EDIDiagnosticsManage;
